import React, {useRef, useEffect} from 'react';
import {
  View,
  StyleSheet,
  Image,
  TouchableOpacity,
  Animated,
  Easing,
  Dimensions,
  Platform,
} from 'react-native';
import {LinearGradient} from 'expo-linear-gradient';
import CCText from '@/Atoms/CCText';
import Box from '@/Atoms/Box';
import {FontSizes} from '@/Utils/Dimensions';
import {useMediaQuery} from '@/Hooks/useMediaQuery/useMediaQuery';
import {useNavigation} from '@react-navigation/native';
import {ScreenName} from '@/navigators/StackConstants';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';
import CCColors from '@/Utils/CCColors';

const {width: screenWidth} = Dimensions.get('window');

const ShimmerOverlay = () => {
  const translateX = useRef(new Animated.Value(-screenWidth))?.current;

  useEffect(() => {
    Animated.loop(
      Animated.sequence([
        Animated.timing(translateX, {
          toValue: screenWidth * 1.5,
          duration: 3500,
          easing: Easing.linear,
          useNativeDriver: true,
        }),
        Animated.delay(500),
      ]),
    ).start();
  }, [translateX]);

  return (
    <Animated.View
      pointerEvents="none"
      style={[
        StyleSheet.absoluteFillObject,
        {
          width: '40%',
          height: '100%',
          opacity: 0.15,
          zIndex: 0,
          transform: [{translateX}, {skewX: '-25deg'}],
        },
      ]}>
      <LinearGradient
        colors={[
          'transparent',
          'rgba(255, 255, 255, 0.02)',
          'rgba(255, 255, 255, 0.6)',
          'rgba(255, 255, 255, 0.02)',
          'transparent',
        ]}
        start={{x: 0, y: 0}}
        end={{x: 1, y: 0}}
        style={{flex: 1}}
      />
    </Animated.View>
  );
};

const GradientText = ({text, style}: {text: string; style?: any}) => {
  if (Platform.OS === 'web') {
    return (
      <CCText
        style={[
          style,
          {
            backgroundImage: 'linear-gradient(90deg, #F97316, #FACC15)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          },
        ]}>
        {text}
      </CCText>
    );
  }
  return <CCText style={[style, {color: '#FACC15'}]}>{text}</CCText>;
};

const GradientBox = ({
  children,
  style,
  contentStyle,
  colors = ['#F97316', '#FACC15'],
}: any) => {
  return (
    <LinearGradient
      colors={colors}
      start={{x: 0, y: 0}}
      end={{x: 1, y: 1}}
      style={[style, {padding: 1.5}]}>
      <View style={[{backgroundColor: '#1E293B', flex: 1}, contentStyle]}>
        {children}
      </View>
    </LinearGradient>
  );
};

interface GlobalSchoolBannerProps {
  onEnroll?: () => void;
  visible?: boolean;
}

const GlobalSchoolBanner: React.FC<GlobalSchoolBannerProps> = ({
  onEnroll,
  visible = true,
}) => {
  const {isMobileOrTab} = useMediaQuery();
  const navigation = useNavigation();

  if (!visible) return null;

  const handleEnroll = () => {
    if (onEnroll) {
      onEnroll();
    } else {
      (navigation as any).navigate(ScreenName.Renewal);
    }
  };

  const renderChessPiecesBackground = () => {
    const scale = isMobileOrTab ? 0.3 : 0.6;
    return (
      <View style={styles.chessPiecesOverlay} pointerEvents="none">
        <Image
          source={require('@/Assets/ChessPieces/White-Pawn.svg')}
          style={[
            styles.chessPieceBg,
            {
              top: 20 * scale,
              left: '5%',
              width: 120 * scale,
              height: 160 * scale,
              transform: [{rotate: '-15deg'}],
            },
          ]}
        />
        <Image
          source={require('@/Assets/ChessPieces/White-Pawn.svg')}
          style={[
            styles.chessPieceBg,
            {
              bottom: 10 * scale,
              left: '15%',
              width: 100 * scale,
              height: 140 * scale,
              transform: [{rotate: '10deg'}],
            },
          ]}
        />
        <Image
          source={require('@/Assets/ChessPieces/White-Bishop.svg')}
          style={[
            styles.chessPieceBg,
            {
              bottom: -40 * scale,
              left: '20%',
              width: 160 * scale,
              height: 220 * scale,
              transform: [{rotate: '25deg'}],
            },
          ]}
        />
        <Image
          source={require('@/Assets/ChessPieces/White-Pawn.svg')}
          style={[
            styles.chessPieceBg,
            {
              top: -30 * scale,
              left: '30%',
              width: 90 * scale,
              height: 120 * scale,
              transform: [{rotate: '-20deg'}],
            },
          ]}
        />
        <Image
          source={require('@/Assets/ChessPieces/White-Rook.svg')}
          style={[
            styles.chessPieceBg,
            {
              top: 30 * scale,
              left: '38%',
              width: 110 * scale,
              height: 160 * scale,
              transform: [{rotate: '-10deg'}],
            },
          ]}
        />
        <Image
          source={require('@/Assets/ChessPieces/White-Knight.svg')}
          style={[
            styles.chessPieceBg,
            {
              bottom: 20 * scale,
              left: '45%',
              width: 130 * scale,
              height: 180 * scale,
              transform: [{rotate: '35deg'}],
            },
          ]}
        />
        <Image
          source={require('@/Assets/ChessPieces/White-Pawn.svg')}
          style={[
            styles.chessPieceBg,
            {
              bottom: -20 * scale,
              left: '50%',
              width: 80 * scale,
              height: 110 * scale,
              transform: [{rotate: '15deg'}],
            },
          ]}
        />
        <Image
          source={require('@/Assets/ChessPieces/White-Knight.svg')}
          style={[
            styles.chessPieceBg,
            {
              top: -20 * scale,
              left: '55%',
              width: 150 * scale,
              height: 200 * scale,
              transform: [{rotate: '-20deg'}],
            },
          ]}
        />
        <Image
          source={require('@/Assets/ChessPieces/White-Rook.svg')}
          style={[
            styles.chessPieceBg,
            {
              bottom: -30 * scale,
              left: '65%',
              width: 120 * scale,
              height: 170 * scale,
              transform: [{rotate: '-15deg'}],
            },
          ]}
        />
        <Image
          source={require('@/Assets/ChessPieces/White-Queen.svg')}
          style={[
            styles.chessPieceBg,
            {
              bottom: -15 * scale,
              left: '72%',
              width: 140 * scale,
              height: 200 * scale,
              transform: [{rotate: '15deg'}],
              opacity: 0.15,
            },
          ]}
        />
        <Image
          source={require('@/Assets/ChessPieces/White-Pawn.svg')}
          style={[
            styles.chessPieceBg,
            {
              top: 20 * scale,
              left: '80%',
              width: 100 * scale,
              height: 130 * scale,
              transform: [{rotate: '20deg'}],
            },
          ]}
        />
        <Image
          source={require('@/Assets/ChessPieces/White-King.svg')}
          style={[
            styles.chessPieceBg,
            {
              top: 10 * scale,
              left: '88%',
              width: 140 * scale,
              height: 190 * scale,
              transform: [{rotate: '30deg'}],
              opacity: 0.15,
            },
          ]}
        />
        <Image
          source={require('@/Assets/ChessPieces/White-Bishop.svg')}
          style={[
            styles.chessPieceBg,
            {
              top: 40 * scale,
              left: '95%',
              width: 120 * scale,
              height: 180 * scale,
              transform: [{rotate: '-25deg'}],
            },
          ]}
        />
      </View>
    );
  };

  const LeftSection = () => (
    <View
      style={{
        flexDirection: 'row',
        gap: 8,
        zIndex: 10,
        alignItems: 'flex-end',
      }}>
      <View style={{flexDirection: 'column', gap: 4, width: 90}}>
        <GradientBox
          style={{borderRadius: 6}}
          contentStyle={{padding: 3, alignItems: 'center', borderRadius: 4.5}}>
          <CCText
            fontSize={6}
            color={'#F97316'}
            fontWeight="bold"
            textAlign="center">
            Exclusive discount for all School of Chess students.
          </CCText>
          <CCText
            fontSize={FontSizes[8]}
            color="#F97316"
            style={{fontWeight: '900'}}>
            50% OFF
          </CCText>
        </GradientBox>

        <View>
          <Image
            source={require('@/Assets/Images/Svgs/gm-shardul-gagare.svg')}
            style={{
              width: 90,
              height: 70,
              resizeMode: 'cover',
              borderRadius: 8,
              borderWidth: 1.5,
              borderColor: '#FACC15',
            }}
          />
          <GradientBox
            style={{borderRadius: 4, marginTop: 3}}
            contentStyle={{padding: 1, borderRadius: 3}}>
            <CCText
              fontSize={FontSizes[isMobileOrTab ? 6 : 8]}
              color="#FFF"
              fontWeight="bold"
              textAlign="center"
              style={{fontStyle: 'italic'}}>
              GM Shardul Gagare
            </CCText>
          </GradientBox>
        </View>
      </View>

      <View style={{flexDirection: 'column', width: 110}}>
        <Image
          source={require('@/Assets/Images/Svgs/gm-elisabeth.svg')}
          style={{
            width: 110,
            height: 110,
            resizeMode: 'cover',
            borderRadius: 10,
            borderWidth: 2,
            borderColor: '#FACC15',
          }}
        />
        <GradientBox
          style={{borderRadius: 6, marginTop: 4}}
          contentStyle={{padding: 3, borderRadius: 5}}>
          <CCText
            fontSize={isMobileOrTab ? 6 : 10}
            color={CCColors?.White}
            fontWeight="bold"
            textAlign="center"
            style={{fontStyle: 'italic'}}>
            GM Elisabeth Pähtz
          </CCText>
        </GradientBox>
      </View>
    </View>
  );

  const CenterSection = () => (
    <Box flex={1} alignItems="center" style={{paddingHorizontal: 8}}>
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          marginBottom: 6,
        }}>
        <Image
          source={require('@/Assets/Images/png/circle_chess_logo.png')}
          style={{width: 70, height: 14, resizeMode: 'contain'}}
        />
        <View
          style={{
            width: 1,
            height: 12,
            backgroundColor: 'rgba(255,255,255,0.3)',
            marginHorizontal: 6,
          }}
        />
        <CCText
          color="#FFF"
          fontWeight="bold"
          fontSize={FontSizes[10]}
          style={{letterSpacing: 0.5}}>
          PLAY TO WIN
        </CCText>
      </View>

      <GradientText
        text={`Recommended for Every Student of School of Chess`}
        style={{
          fontSize: FontSizes[isMobileOrTab ? 18 : 28],
          fontWeight: '900',
          textAlign: 'center',
          marginBottom: 12,
          lineHeight: isMobileOrTab ? 18 : 28,
        }}
      />

      <View
        style={{
          flexDirection: 'row',
          gap: 4,
          marginBottom: 12,
          flexWrap: 'nowrap',
          justifyContent: 'center',
        }}>
        <GradientBox
          colors={['#4CAF50', '#2E7D32']}
          style={{borderRadius: 12}}
          contentStyle={{
            paddingHorizontal: 8,
            paddingVertical: 2,
            borderRadius: 10.5,
          }}>
          <CCText fontSize={FontSizes[7]} color="#4CAF50" fontWeight="bold">
            Must for tournament players
          </CCText>
        </GradientBox>
        <GradientBox
          colors={['#4CAF50', '#2E7D32']}
          style={{borderRadius: 12}}
          contentStyle={{
            paddingHorizontal: 8,
            paddingVertical: 2,
            borderRadius: 10.5,
          }}>
          <CCText fontSize={FontSizes[7]} color="#4CAF50" fontWeight="bold">
            Essential for self-improvers
          </CCText>
        </GradientBox>
      </View>

      <View
        style={{
          flexDirection: 'row',
          gap: 4,
          flexWrap: 'nowrap',
          justifyContent: 'center',
        }}>
        {['London System', 'Sicilian Defense', 'Italian Game'].map(tag => (
          <GradientBox
            key={tag}
            style={{borderRadius: 12}}
            contentStyle={{
              paddingHorizontal: 8,
              paddingVertical: 2,
              borderRadius: 10.5,
            }}>
            <CCText fontSize={FontSizes[7]} color="#FACC15" fontWeight="bold">
              {tag}
            </CCText>
          </GradientBox>
        ))}
      </View>
    </Box>
  );

  const RightSection = () => (
    <View style={styles.rightColumn}>
      <GradientBox
        style={{borderRadius: 6}}
        contentStyle={{
          paddingHorizontal: 8,
          paddingVertical: 3,
          alignItems: 'center',
          borderRadius: 4.5,
        }}>
        <CCText fontSize={FontSizes[10]} color="#FACC15" fontWeight="bold">
          Starts{' '}
          <CCText
            fontSize={FontSizes[15]}
            color="#FACC15"
            style={{fontWeight: '900'}}>
            6th May
          </CCText>
        </CCText>
      </GradientBox>
      <View
        style={{position: 'relative', height: 45, justifyContent: 'center'}}>
        <GradientBox
          style={{borderRadius: 6, marginLeft: 20}}
          contentStyle={{
            paddingLeft: 25,
            paddingRight: 10,
            paddingVertical: 3,
            flexDirection: 'row',
            alignItems: 'center',
            borderRadius: 4.5,
          }}>
          <View>
            <CCText fontSize={10} color="#FACC15" fontWeight="bold">
              Starting At
            </CCText>
            <CCText
              fontSize={FontSizes[15]}
              color="#F97316"
              style={{fontWeight: '900'}}>
              ₹49/{' '}
              <CCText
                fontSize={FontSizes[10]}
                color="#FACC15"
                style={{fontWeight: '900'}}>
                Class
              </CCText>
            </CCText>
          </View>
        </GradientBox>
        <Image
          source={require('@/Assets/Images/Svgs/chess_pieces.svg')}
          style={{
            width: 42,
            height: 47,
            position: 'absolute',
            resizeMode: 'contain',
          }}
        />
      </View>
      <TouchableOpacity onPress={handleEnroll} activeOpacity={0.8}>
        <GradientBox
          style={{borderRadius: 6}}
          contentStyle={{
            paddingHorizontal: 10,
            paddingVertical: 5,
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderRadius: 4.5,
          }}>
          <CCText
            fontSize={FontSizes[13]}
            color="#FACC15"
            style={{fontWeight: '900'}}>
            Enroll Now
          </CCText>
          <View
            style={{
              backgroundColor: '#FACC15',
              borderRadius: 8,
              padding: 2,
              marginLeft: 2,
            }}>
            <MaterialCommunityIcons
              name="arrow-top-right"
              size={12}
              color="#1E293B"
            />
          </View>
        </GradientBox>
      </TouchableOpacity>
    </View>
  );

  if (isMobileOrTab) {
    return (
      <Box style={styles.mobileMainContainer} onPress={handleEnroll}>
        <View style={styles.mobileTopSection}>
          <ShimmerOverlay />
          {renderChessPiecesBackground()}
          <View style={styles.mobileHeader}>
            <Image
              source={require('@/Assets/Images/png/circle_chess_logo.png')}
              style={{width: 80, height: 20, resizeMode: 'contain'}}
            />
            <View style={styles.verticalDivider} />
            <CCText color="#FFF" fontWeight="bold" fontSize={14}>
              PLAY TO WIN
            </CCText>
          </View>

          <GradientText
            text="Recommended for Every Student of School of Chess"
            style={styles.mobileTitleText}
          />

          <View style={styles.mobileTagRow}>
            <GradientBox
              colors={['#4CAF50', '#2E7D32']}
              style={{borderRadius: 12}}
              contentStyle={{
                paddingHorizontal: 8,
                paddingVertical: 2,
                borderRadius: 10.5,
              }}>
              <CCText fontSize={7} color="#4CAF50" fontWeight="bold">
                Must for tournament players
              </CCText>
            </GradientBox>
            <GradientBox
              colors={['#4CAF50', '#2E7D32']}
              style={{borderRadius: 12}}
              contentStyle={{
                paddingHorizontal: 8,
                paddingVertical: 2,
                borderRadius: 10.5,
              }}>
              <CCText fontSize={7} color="#4CAF50" fontWeight="bold">
                Essential for self-improvers
              </CCText>
            </GradientBox>
          </View>

          <View style={styles.mobileTagRow}>
            {['London System', 'Sicilian Defense', 'Italian Game'].map(tag => (
              <GradientBox
                key={tag}
                style={{borderRadius: 12}}
                contentStyle={{
                  paddingHorizontal: 8,
                  paddingVertical: 2,
                  borderRadius: 10.5,
                }}>
                <CCText fontSize={7} color="#FACC15" fontWeight="bold">
                  {tag}
                </CCText>
              </GradientBox>
            ))}
          </View>
        </View>

        <View style={styles.mobileBottomBar}>
          <View style={styles.bottomBox}>
            <CCText fontSize={8} color="#1E293B" fontWeight="bold">
              Starts
            </CCText>
            <CCText fontSize={10} color="#1E293B" style={{fontWeight: '900'}}>
              6th May
            </CCText>
          </View>
          <View
            style={[styles.bottomBox, {flexDirection: 'row', paddingLeft: 20}]}>
            <Image
              source={require('@/Assets/Images/Svgs/chess_pieces.svg')}
              style={styles.bottomChessPieces}
            />
            <View>
              <CCText fontSize={6} color="#1E293B" fontWeight="bold">
                Starting At
              </CCText>
              <CCText fontSize={10} color="#1E293B" style={{fontWeight: '900'}}>
                ₹49/{' '}
                <CCText fontSize={8} color="#1E293B">
                  Class
                </CCText>
              </CCText>
            </View>
          </View>

          <View style={[styles.bottomBox, {paddingHorizontal: 4}]}>
            <CCText
              fontSize={6}
              color="#1E293B"
              fontWeight="bold"
              textAlign="center">
              Exclusive discount for School of Chess students.
            </CCText>
            <CCText fontSize={8} color="#1E293B" style={{fontWeight: '900'}}>
              50% OFF
            </CCText>
          </View>

          <TouchableOpacity
            onPress={handleEnroll}
            style={styles.bottomEnrollBtn}>
            <CCText fontSize={10} color="#1E293B" style={{fontWeight: '900'}}>
              Enroll Now
            </CCText>
            <View style={styles.arrowIconBg}>
              <MaterialCommunityIcons
                name="arrow-top-right"
                size={10}
                color="#FACC15"
              />
            </View>
          </TouchableOpacity>
        </View>
      </Box>
    );
  }

  return (
    <Box style={styles.container} onPress={handleEnroll}>
      <ShimmerOverlay />
      {renderChessPiecesBackground()}
      <LeftSection />
      <CenterSection />
      <RightSection />
    </Box>
  );
};

const styles = StyleSheet.create({
  container: {
    width: '100%',
    borderRadius: 12,
    backgroundColor: '#1E293B',
    overflow: 'hidden',
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: '4%',
    paddingVertical: 8,
    position: 'relative',
    minHeight: 120,
  },
  mobileMainContainer: {
    width: '100%',
    borderRadius: 12,
    backgroundColor: '#1E293B',
    overflow: 'hidden',
    position: 'relative',
  },
  mobileTopSection: {
    padding: 12,
    alignItems: 'center',
    zIndex: 10,
  },
  mobileHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 10,
  },
  verticalDivider: {
    width: 1.5,
    height: 16,
    backgroundColor: 'rgba(255,255,255,0.4)',
    marginHorizontal: 10,
  },
  mobileTitleText: {
    fontSize: 24,
    fontWeight: '900',
    textAlign: 'center',
    marginBottom: 12,
    lineHeight: 28,
  },
  mobileTagRow: {
    flexDirection: 'row',
    gap: 6,
    marginBottom: 6,
    justifyContent: 'center',
  },
  tagPill: {
    borderRadius: 100,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderWidth: 1.5,
    backgroundColor: 'rgba(255,255,255,0.05)',
  },
  mobileBottomBar: {
    backgroundColor: '#FACC15',
    flexDirection: 'row',
    padding: 6,
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 4,
  },
  bottomBox: {
    flex: 1,
    height: 38,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1.5,
    borderColor: '#1E293B',
    borderRadius: 8,
    position: 'relative',
  },
  bottomChessPieces: {
    width: 25,
    height: 28,
    position: 'absolute',
    left: 0,
    bottom: -2,
    resizeMode: 'contain',
    zIndex: 20,
  },
  bottomEnrollBtn: {
    flex: 1.2,
    height: 38,
    backgroundColor: 'transparent',
    borderRadius: 12,
    borderWidth: 2,
    borderColor: '#1E293B',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 4,
  },
  arrowIconBg: {
    backgroundColor: '#1E293B',
    borderRadius: 100,
    padding: 3,
    marginLeft: 6,
    width: 20,
    height: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  rightColumn: {
    flexDirection: 'column',
    gap: 8,
    zIndex: 10,
    width: 140,
  },
  chessPiecesOverlay: {
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    right: 0,
    width: '100%',
    zIndex: 1,
    overflow: 'hidden',
  },
  chessPieceBg: {
    position: 'absolute',
    opacity: 0.08,
    resizeMode: 'contain',
    tintColor: '#FFFFFF',
  },
});

export default GlobalSchoolBanner;
